import { create } from 'zustand';

import { AIJobType } from 'models/AIJob';

export enum AIJobNotificationType {
  ADMISSION_SUMMARY = 'admissionSummary',
  AUTHORIZATION_SUMMARY = 'authorizationSummary',
  AUTHORIZATION_COMPARISON = 'authorizationComparison',
  REFERRAL_SUMMARY = 'referralSummary',
  WORKSPACE_SUMMARY = 'workspaceSummary',
}

export type TrackedJob = {
  id: string;
  type: AIJobType;
};

export type AIJobNotificationContext = {
  jobs: TrackedJob[];
  jobType: AIJobNotificationType;
  summaryType?: string;
  actionSuccessText?: string;
  actionFailureText?: string;
  jobbableId: string;
  patientName?: string;
  returnUrl: string; // also the URL we check to see if we need to hide the notification
  createdAt: Date;
};

export const getTrackingKey = (context: Pick<AIJobNotificationContext, 'jobbableId' | 'jobType'>) =>
  `${context.jobbableId}:${context.jobType}`;

type AIJobNotificationStore = {
  // Active job tracking (keyed by jobbableId:jobType)
  trackedJobs: Map<string, AIJobNotificationContext>;

  // Dismissed jobs that are still processing
  // When these jobs complete/error, we re-show the notification
  dismissedProcessingJobs: Set<string>;

  // Notification ID mapping (trackingKey -> notificationId)
  jobNotificationMap: Map<string, string>;

  // Actions
  trackAIJob: (context: AIJobNotificationContext) => void;
  untrackAIJob: (trackingKey: string) => void;
  updateTrackedAIJob: (
    trackingKey: string,
    newContext: Partial<Omit<AIJobNotificationContext, 'jobs' | 'jobType' | 'jobbableId'>>
  ) => void;
  dismissJobNotification: (trackingKey: string, isProcessing: boolean) => void;
  addJobNotificationMapping: (trackingKey: string, notificationId: string) => void;
  removeJobNotificationMapping: (trackingKey: string) => void;
  clearDismissedProcessingJob: (trackingKey: string) => void;
};

export const useAIJobNotificationStore = create<AIJobNotificationStore>((set) => ({
  trackedJobs: new Map(),
  dismissedProcessingJobs: new Set(),
  jobNotificationMap: new Map(),

  trackAIJob: (context) => {
    set((state) => {
      const key = getTrackingKey(context);
      if (state.trackedJobs.has(key)) return state;

      const newTrackedJobs = new Map(state.trackedJobs);
      newTrackedJobs.set(key, context);
      return { trackedJobs: newTrackedJobs };
    });
  },

  untrackAIJob: (trackingKey: string) => {
    set((state) => {
      const newTrackedJobs = new Map(state.trackedJobs);
      const newJobNotificationMap = new Map(state.jobNotificationMap);
      const newDismissedProcessingJobs = new Set(state.dismissedProcessingJobs);

      newTrackedJobs.delete(trackingKey);
      newJobNotificationMap.delete(trackingKey);
      newDismissedProcessingJobs.delete(trackingKey);

      return {
        trackedJobs: newTrackedJobs,
        jobNotificationMap: newJobNotificationMap,
        dismissedProcessingJobs: newDismissedProcessingJobs,
      };
    });
  },

  updateTrackedAIJob(trackingKey, newContext) {
    set((state) => {
      const existingJob = state.trackedJobs.get(trackingKey);
      if (!existingJob) return state;

      const newTrackedJobs = new Map(state.trackedJobs);
      newTrackedJobs.set(trackingKey, { ...existingJob, ...newContext });
      return { trackedJobs: newTrackedJobs };
    });
  },

  dismissJobNotification: (trackingKey, isProcessing) => {
    set((state) => {
      const newDismissedProcessingJobs = new Set(state.dismissedProcessingJobs);

      if (isProcessing) {
        newDismissedProcessingJobs.add(trackingKey);
      } else {
        // Job already complete/errored, just clean up
        const newTrackedJobs = new Map(state.trackedJobs);
        const newJobNotificationMap = new Map(state.jobNotificationMap);
        newTrackedJobs.delete(trackingKey);
        newJobNotificationMap.delete(trackingKey);

        return {
          trackedJobs: newTrackedJobs,
          jobNotificationMap: newJobNotificationMap,
        };
      }

      return { dismissedProcessingJobs: newDismissedProcessingJobs };
    });
  },

  addJobNotificationMapping: (trackingKey, notificationId) => {
    set((state) => {
      const newJobNotificationMap = new Map(state.jobNotificationMap);
      newJobNotificationMap.set(trackingKey, notificationId);
      return { jobNotificationMap: newJobNotificationMap };
    });
  },

  removeJobNotificationMapping: (trackingKey) => {
    set((state) => {
      const newJobNotificationMap = new Map(state.jobNotificationMap);
      newJobNotificationMap.delete(trackingKey);
      return { jobNotificationMap: newJobNotificationMap };
    });
  },

  clearDismissedProcessingJob: (trackingKey) => {
    set((state) => {
      const newDismissedProcessingJobs = new Set(state.dismissedProcessingJobs);
      newDismissedProcessingJobs.delete(trackingKey);
      return { dismissedProcessingJobs: newDismissedProcessingJobs };
    });
  },
}));
